import { z } from 'zod'

import type { InternalOpenApiNavigationEntry } from '@/schema/navigation/openapi'
import type { InternalPageNavigationEntry } from '@/schema/navigation/pages'

import {
  type InternalNavigationConfig,
  internalNavigationConfigSchema,
  type NavigationConfig,
  navigationConfigSchema,
} from './navigation/navigation'
import { type SiteConfig, siteConfigSchema } from './site-config'

// ---------------------------------------------------------------------------
// Config Schema

export const schemaCdn = 'https://cdn.scalar.com/schema/scalar-config-next.json'

export type BaseScalarConfig = {
  $schema: string
  scalar: '2.0.0'
  info?: {
    title?: string
    description?: string
    version?: string
  }
  root?: string
  publishOnMerge?: boolean
  publishPreviews?: boolean
  pullRequestComments?: boolean
  insertPageTitles?: boolean
  assetsDir?: string
  siteConfig: SiteConfig
}

export const baseScalarConfigSchema = z.object({
  $schema: z.string().url().default(schemaCdn),
  /** Version of the Scalar config schema. */
  scalar: z
    .literal('2.0.0')
    .default('2.0.0')
    .describe('The version of the Scalar config schema.'),
  /** Standard JSON document info object */
  info: z
    .object({
      title: z.string().optional(),
      description: z.string().optional(),
      version: z.string().optional(),
    })
    .optional(),
  /** Optional root directory specification relative to the config file. */
  root: z
    .string()
    .optional()
    .describe(
      'Optional root directory specification relative to the config file.',
    ),
  /** Whether to publish the site on merge to the configured git branch. */
  publishOnMerge: z
    .boolean()
    .optional()
    .describe(
      'Whether to publish the site on merge to the configured git branch.',
    ),
  /** Whether to enable preview deployments for docs changes in any git branch. */
  publishPreviews: z
    .boolean()
    .optional()
    .describe(
      'Whether to enable preview deployments for docs changes in any git branch.',
    ),
  /** Whether to enable PR comments for publishing updates, such as publish previews. */
  pullRequestComments: z
    .boolean()
    .optional()
    .describe(
      'Whether to enable PR comments for publishing updates, such as publish previews.',
    ),
  /** Whether to insert an H1 with Title and Description at top of each guide page, or not. Deprecated. */
  insertPageTitles: z
    .boolean()
    .optional()
    .describe(
      'Whether to insert an H1 with Title and Description at top of each guide page, or not. Deprecated. Write the title and description in the markdown instead.',
    ),
  assetsDir: z
    .string()
    .optional()
    .describe(
      'Specify an assets folder to serve custom assets such as media files. These are served globally from the root path, e.g. /picture.png',
    ),
  siteConfig: siteConfigSchema,
}) satisfies z.ZodType<BaseScalarConfig>

export const externalScalarConfigSchema = baseScalarConfigSchema.extend({
  $schema: z
    .string()
    .url()
    .default('https://cdn.scalar.com/schema/scalar-config-next.json'),
}) satisfies z.ZodType<BaseScalarConfig>

export type ScalarConfigMultiVersion = BaseScalarConfig & {
  versions: Record<string, NavigationConfig>
}

/** If there are multiple versions we will have a navigation config for each version */
const scalarConfigMultiVersionSchema = externalScalarConfigSchema.extend({
  versions: z.record(z.string(), navigationConfigSchema),
}) satisfies z.ZodType<ScalarConfigMultiVersion>

export type ScalarConfigSingleVersion = BaseScalarConfig & {
  navigation: NavigationConfig
}

/** If there is a single version we will have a single navigation config that we will hoist during parsing */
const scalarConfigSchemaSingleVersion = externalScalarConfigSchema.extend({
  navigation: navigationConfigSchema,
}) satisfies z.ZodType<ScalarConfigSingleVersion>

export type ScalarConfig = ScalarConfigMultiVersion | ScalarConfigSingleVersion

/** We accept simplified config for single version, but we will hoist the navigation config to the versions object */
export const scalarConfigSchema: z.ZodType<ScalarConfig> = z.union([
  scalarConfigMultiVersionSchema,
  scalarConfigSchemaSingleVersion,
])

/** Top Level Scalar Config Object */
export type InternalScalarConfig = BaseScalarConfig & {
  versions: {
    [x: string]: InternalNavigationConfig
    default: InternalNavigationConfig
  }
}

export const internalScalarConfigSchema: z.ZodType<InternalScalarConfig> =
  baseScalarConfigSchema.extend({
    versions: z
      .object({
        default: internalNavigationConfigSchema,
      })
      .catchall(internalNavigationConfigSchema),
  })

export type ResolvedPage =
  | InternalOpenApiNavigationEntry
  | InternalPageNavigationEntry
